import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetUniqueCoachVenueCourtsLazyQuery } from 'types/generated/client';
import { VenueAddress, formatCoachVenueData } from 'utils/shared/coachBuilder';
import { addOneHour } from 'utils/shared/date/addHourToSlot';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { useGeoLocation } from 'hooks/useGeoLocation';
import ChevronLeft from 'svg/ChevronLeft';
import ChevronRight from 'svg/ChevronRight';
import { Coach } from 'screens/CoachDetails/types';
import { CUSTOM_COURT_IMG_PLACEHOLDER_SRC } from 'screens/PlayerHome/utils';
import { LocationAndTimeProps } from 'screens/PlayerLoggedInScreen/props';
import { TimePicker } from 'components/CalendarDatePicker/CalendarDatePicker';
import { VenueCard } from 'components/CalendarDatePicker/VenueSlider';
import CardsSection from 'components/CardSection';

const LocationAndTime: React.FC<LocationAndTimeProps> = ({ coach, selectedDate, onNext }) => {
  const [venues, setVenues] = useState<VenueAddress[] | null>(null);
  const [selectedVenues, setSelectedVenues] = useState<Map<string, VenueAddress>>(new Map());

  const prevArrowRef = useRef<HTMLDivElement>(null);
  const nextArrowRef = useRef<HTMLDivElement>(null);

  const { centerLatitude, centerLongitude } = useGeoLocation();

  const [getUniqueCoachVenueCourts, { data: uniqueCoachVenueCourtsData, loading: fetchingVenues }] =
    useGetUniqueCoachVenueCourtsLazyQuery();

  const getIsVenueSelected = (venue: VenueAddress): boolean => !!selectedVenues?.get(venue.id);

  const getArrow = (type: 'prev' | 'next') => {
    if (type === 'prev') {
      return (
        <div
          ref={prevArrowRef}
          className="grid h-10 w-10 place-items-center rounded-full bg-color-bg-lightmode-primary shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:bg-color-bg-lightmode-primary"
        >
          <ChevronLeft className="h-4 w-4" />
        </div>
      );
    } else {
      return (
        <div
          ref={nextArrowRef}
          className="grid h-10 w-10 place-items-center rounded-full bg-color-bg-lightmode-primary shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:bg-color-bg-lightmode-primary"
        >
          <ChevronRight className="h-4 w-4" />
        </div>
      );
    }
  };

  const coachAvailability = useMemo(
    () =>
      coach.coachAvailability.filter((availability) => {
        const venueId = availability.coachAvailabilityVenueId?.id;
        const customCourtId = availability.coachAvailabilityCustomCourt?.id;
        return selectedVenues?.get(venueId || customCourtId);
      }),
    [selectedVenues, coach],
  );

  const handleVenueSelection = (venue: VenueAddress) => () => {
    setSelectedVenues((prevSelectedVenues) => {
      const selectedVenuesCopy = new Map(prevSelectedVenues);
      if (selectedVenuesCopy.has(venue.id)) {
        selectedVenuesCopy.delete(venue.id);
      } else {
        selectedVenuesCopy.set(venue.id, venue);
      }
      return selectedVenuesCopy;
    });
  };

  useEffect(() => {
    if (!venues) return;
    const venuesMap = new Map<string, VenueAddress>();
    venues.forEach((venue) => {
      venuesMap.set(venue.id, venue);
    });
    setSelectedVenues(venuesMap);
  }, [venues]);

  useEffect(() => {
    const init = async () => {
      const data = await getUniqueCoachVenueCourts({
        fetchPolicy: 'cache-and-network',
        variables: {
          userId: coach.id,
        },
      });
      if (data?.data) {
        const venues = formatCoachVenueData(data.data.coachAvailability);
        setVenues(venues);
      }
    };
    coach.id && init();
  }, [uniqueCoachVenueCourtsData, coach.id]);

  return (
    <div className="flex h-full flex-col py-6">
      <div>
        <div className="mb-4 flex justify-end gap-2 px-ds-xl max-sm:hidden max-sm:px-ds-lg">
          {getArrow('prev')}
          {getArrow('next')}
        </div>
        <div className="pl-4">
          <CardsSection
            gap="1rem"
            scrollOffset={{
              base: 100,
            }}
            visibleCards={{
              base: 2,
              lg: 3,
              xl: 3,
              xxl: 3,
            }}
            loading={fetchingVenues}
            externalNextArrowRef={nextArrowRef}
            externalPrevArrowRef={prevArrowRef}
            data={venues || []}
            renderCard={(venue) => {
              const isSelected = getIsVenueSelected(venue);
              return (
                // border selection
                <div className="p-1">
                  <VenueCard
                    key={venue.id}
                    isSelected={isSelected}
                    handleVenueSelection={handleVenueSelection}
                    venue={venue}
                    customCourtImgPlaceholderSrc={CUSTOM_COURT_IMG_PLACEHOLDER_SRC}
                    className="!w-full"
                    distance={Math.round(
                      calculateHaversineDistance({
                        coord1: {
                          latitude: venue.latitude || 0,
                          longitude: venue.longitude || 0,
                        },
                        coord2: {
                          latitude: centerLatitude || 0,
                          longitude: centerLongitude || 0,
                        },
                        unit: 'miles',
                      }),
                    )}
                  />
                </div>
              );
            }}
          />
        </div>
      </div>
      <div className="mt-8 min-h-0 flex-1">
        <TimePicker
          selectedDate={selectedDate}
          selectTimeSlot={(date, time, location) =>
            onNext({
              location,
              currentDateTime: {
                date,
                startTime: time,
                endTime: addOneHour(time),
              },
            })
          }
          coach={coach as Coach}
          lessons={coach.coachLessons}
          coachAvailability={coachAvailability}
          selectedVenues={selectedVenues}
        />
      </div>
    </div>
  );
};

export default LocationAndTime;
